/**
 * Reading a table out of whatever the office already has.
 *
 * A customer list arrives as a CSV exported from the system being left behind,
 * as a semicolon file from a European edition of Excel, or — most often, in
 * practice — as a block of cells selected in a spreadsheet and pasted straight
 * into the browser, which the clipboard hands over tab-separated. All three are
 * the same shape of thing, so all three are parsed here rather than asking the
 * person which one they have: the delimiter is something a computer can work
 * out, and asking is one more question between them and their data.
 */

/**
 * A row of a table, and where in the source it came from.
 *
 * The line number travels with the row because both readers lose it otherwise:
 * a delimited file has blank lines dropped out of the middle, and a worksheet
 * simply omits its empty rows. Recomputing a position afterwards points every
 * later message at the wrong line of the file the person is looking at.
 */
export interface TableRow {
  /** 1-based, as the file or the spreadsheet numbers it. */
  line: number;
  cells: string[];
}

/**
 * The separators worth guessing between.
 *
 * Tab leads because a paste is the one case where the delimiter is certain
 * rather than sniffed, and because a tab inside a spreadsheet cell is not
 * something a person can type — where tabs appear at all, they are structure.
 */
export const CANDIDATE_DELIMITERS = ['\t', ',', ';', '|'] as const;

export type Delimiter = (typeof CANDIDATE_DELIMITERS)[number];

/** How much of a file the delimiter guess reads before committing. */
const SNIFF_LINES = 20;

/**
 * Splits delimited text into rows of raw cells, RFC 4180 style.
 *
 * Quoted fields may contain the delimiter, line breaks, and doubled quotes;
 * everything outside quotes is taken literally. CRLF, LF and lone-CR line
 * endings are all accepted because all three turn up — the last from
 * spreadsheets old enough to have been written on a Mac.
 *
 * Rows are returned ragged, exactly as long as the source made them. Squaring
 * them up is the caller's decision: a short row usually means trailing empty
 * cells, but a row that is short because a quote was left open is a different
 * problem, and flattening both into the same shape here would hide it.
 */
export function parseDelimited(text: string, delimiter?: string, options: { maxRows?: number } = {}): string[][] {
  return parseTable(text, delimiter, options).rows;
}

export interface ParsedTable {
  rows: string[][];
  /**
   * The text ran out inside a quoted field.
   *
   * Never a detail to shrug at: everything after the unclosed quote — every
   * remaining line of the file — has been folded into that one cell. A caller
   * that ignores this imports one corrupted customer and loses the rest of the
   * list without anything having gone visibly wrong.
   */
  unterminatedQuote: boolean;
  /**
   * `maxRows` stopped the parse before the end of the text, so there was more
   * and this is not all of it.
   */
  stoppedEarly: boolean;
}

/**
 * The same parse, with the two things that make a short answer a wrong one.
 *
 * `parseDelimited` is the shape most callers want; this is the one the importer
 * uses, because "here are some rows" and "here are all the rows, and they were
 * well-formed" are different claims and only the second is safe to import.
 */
export function parseTable(text: string, delimiter?: string, options: { maxRows?: number } = {}): ParsedTable {
  const sep = delimiter ?? sniffDelimiter(text);
  const maxRows = options.maxRows ?? Number.POSITIVE_INFINITY;
  // A byte-order mark survives every round trip through Excel and would
  // otherwise become part of the first header, which is how a perfectly
  // ordinary "First Name" column stops matching anything.
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  let index = 0;

  const endCell = () => {
    row.push(cell);
    cell = '';
  };
  const endRow = () => {
    endCell();
    rows.push(row);
    row = [];
  };

  while (index < source.length) {
    const char = source[index]!;

    if (quoted) {
      if (char === '"') {
        // A doubled quote is one literal quote; a single one closes the field.
        if (source[index + 1] === '"') {
          cell += '"';
          index += 2;
          continue;
        }
        quoted = false;
        index += 1;
        continue;
      }
      cell += char;
      index += 1;
      continue;
    }

    if (char === '"' && cell.length === 0) {
      quoted = true;
      index += 1;
      continue;
    }

    if (char === sep) {
      endCell();
      index += 1;
      continue;
    }

    if (char === '\r' || char === '\n') {
      endRow();
      if (rows.length >= maxRows) return { rows, unterminatedQuote: false, stoppedEarly: true };
      // CRLF is one line ending, not two.
      index += char === '\r' && source[index + 1] === '\n' ? 2 : 1;
      continue;
    }

    cell += char;
    index += 1;
  }

  // A file ending in a newline has already closed its last row; anything still
  // buffered is a final row that simply was not terminated.
  if (cell.length > 0 || row.length > 0) endRow();

  // Still quoted at the end means the closing quote never came, and everything
  // after the opening one is sitting in that last cell rather than in the rows
  // it belongs to.
  return { rows, unterminatedQuote: quoted, stoppedEarly: false };
}

/**
 * Guesses which separator a block of text is using.
 *
 * The signal is consistency rather than frequency: a customer list has the same
 * number of columns on every line, so the right delimiter is the one that cuts
 * every line into the same number of pieces. Counting occurrences instead would
 * pick the comma out of "Okafor, Dana" in a tab-separated paste.
 *
 * Falls back to a comma, which is what a file named `.csv` is until proven
 * otherwise.
 */
export function sniffDelimiter(text: string): Delimiter {
  const sample = parseSampleLines(text, SNIFF_LINES);
  if (sample.length === 0) return ',';

  let best: { delimiter: Delimiter; score: number } = { delimiter: ',', score: 0 };
  for (const delimiter of CANDIDATE_DELIMITERS) {
    const counts = sample.map((line) => parseDelimited(line, delimiter)[0]?.length ?? 0);
    const modal = modalValue(counts);
    // One column means this delimiter never appeared — every line is intact.
    if (modal < 2) continue;
    const agreement = counts.filter((count) => count === modal).length / counts.length;
    // Agreement decides; column count only breaks ties, and is capped so that a
    // delimiter which shreds every line into dozens of pieces cannot win on
    // width alone.
    const score = agreement * 100 + Math.min(modal, 20);
    if (score > best.score) best = { delimiter, score };
  }

  return best.delimiter;
}

/**
 * The first `limit` logical lines, with line breaks inside quoted cells kept
 * whole — a note field containing a paragraph must not read as five rows that
 * disagree about their column count.
 */
function parseSampleLines(text: string, limit: number): string[] {
  const lines: string[] = [];
  let current = '';
  let quoted = false;

  for (let index = 0; index < text.length && lines.length < limit; index += 1) {
    const char = text[index]!;
    if (char === '"') quoted = !quoted;
    if (!quoted && (char === '\n' || char === '\r')) {
      if (current.trim().length > 0) lines.push(current);
      current = '';
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      continue;
    }
    current += char;
  }
  if (current.trim().length > 0 && lines.length < limit) lines.push(current);

  return lines;
}

function modalValue(values: readonly number[]): number {
  const tally = new Map<number, number>();
  for (const value of values) tally.set(value, (tally.get(value) ?? 0) + 1);
  let modal = 0;
  let best = 0;
  for (const [value, count] of tally) {
    if (count > best || (count === best && value > modal)) {
      modal = value;
      best = count;
    }
  }
  return modal;
}

/**
 * Undoes the formula guard a delimited export writes.
 *
 * Exports — ours included (see `csvResponse`) — prefix a leading `=`, `+`, `-`
 * or `@` with an apostrophe so the receiving spreadsheet opens the value as
 * text instead of evaluating it. Re-importing the same file would otherwise
 * keep the apostrophe forever, one per round trip.
 *
 * Exactly one apostrophe comes off, and only in front of a character the guard
 * would have been written for — including another apostrophe, which is how a
 * value that genuinely starts with one survives the round trip rather than
 * being read as a guard and losing a character.
 *
 * For delimited text only. A worksheet cell is never guarded: an xlsx stores
 * text as text, so nothing there could have added this and stripping it would
 * simply delete a character somebody typed.
 */
export function unguardCell(value: string): string {
  return /^'(?:['=+\-@\t\r\n]|\s+[=+\-@])/.test(value) ? value.slice(1) : value;
}

/**
 * Whether a row holds nothing at all.
 *
 * Spreadsheets are full of these: a blank line separating sections, or the
 * trailing rows a selection picked up below the last real one. They are not
 * import failures, so nothing should report them as such.
 */
export function isBlankRow(row: readonly string[]): boolean {
  return row.every((cell) => cell.trim().length === 0);
}
